import { EditIcon, TrashIcon } from "@/assets/icons";
import { isNotAdminAreaLevel } from "@/helpers/datasetAttributeHelper";
import { formatDecimalNumber } from "@/helpers/numberHelpers";
import { DatasetFilter, FilterOption } from "@/types/dataset";

import dayjs from "dayjs";

type SavedFiltersListProps = {
  filters: DatasetFilter[];
  filterOptions: FilterOption[];
  onEdit?: (filter: DatasetFilter) => void;
  onDelete?: (filter: DatasetFilter) => void;
};

const SavedFiltersList = ({
  filterOptions,
  filters,
  onEdit,
  onDelete
}: SavedFiltersListProps) => {
  const renderFilterContent = (filter: DatasetFilter) => {
    if (filter.dateTimeRange) {
      const { from, to } = filter.dateTimeRange;
      return (
        <div className="text-xs text-txtPlaceholder font-normal">
          <div>
            From{" "}
            {from.date && from.time
              ? dayjs(`${from.date} ${from.time}`).format("D MMM YYYY, HH:mm")
              : ""}
            ,
          </div>
          <div>
            Until{" "}
            {to.date && to.time
              ? dayjs(`${to.date} ${to.time}`).format("D MMM YYYY, HH:mm")
              : ""}
          </div>
        </div>
      );
    }

    if (filter.dateRange) {
      const { from, to } = filter.dateRange;
      return (
        <div className="text-xs text-txtPlaceholder font-normal">
          <div>From {from ? dayjs(`${from}`).format("D MMM YYYY") : ""},</div>
          <div>Until {to ? dayjs(`${to}`).format("D MMM YYYY") : ""}</div>
        </div>
      );
    }

    if (filter.timeRange) {
      const { from, to } = filter.timeRange;
      return (
        <div className="text-xs text-txtPlaceholder font-normal">
          <div>
            From {from ?? ""}, Until {to ?? ""}
          </div>
        </div>
      );
    }

    if (filter.minMaxRange) {
      return (
        <div className="text-sm text-txtBody">
          <span>
            Min. {formatDecimalNumber(filter.minMaxRange?.min)}, Max.{" "}
            {formatDecimalNumber(filter.minMaxRange?.max)}
          </span>
        </div>
      );
    }

    return (
      <div className="text-xs text-txtPlaceholder">
        {filter?.selectedOptions?.length} Options Selected
      </div>
    );
  };

  return (
    <div className="space-y-3 font-sans" data-testid="saved-filters-list">
      {filters
        .filter((filter) => {
          return isNotAdminAreaLevel(filter.attributeCode);
        })
        .map((filter) => {
          return (
            <div
              key={
                filter.attributeCode
                  ? `${filter.filterId}_${filter.attributeCode}`
                  : filter.filterId
              }
              className="p-3 bg-white rounded-xl border border-gainsboro flex flex-row items-center justify-between"
            >
              <div>
                <div className="flex justify-between items-start mb-1">
                  <div className="text-xs text-txtHeading font-semibold">
                    {filter.filterLabel ||
                      filterOptions.find(
                        (option) => option.id === filter.filterId
                      )?.label}
                  </div>
                </div>
                {renderFilterContent(filter)}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => onEdit?.(filter)}
                  className="hover:bg-gray-50 rounded-md transition-colors"
                >
                  <EditIcon
                    className=" fill-txtBody"
                    title="Edit Saved Filter List Button"
                  />
                </button>
                <button
                  onClick={() => onDelete?.(filter)}
                  className=" hover:bg-red-50 rounded-md transition-colors"
                >
                  <TrashIcon
                    className=" fill-red-500"
                    title="Delete Saved Filter List Button"
                  />
                </button>
              </div>
            </div>
          );
        })}
    </div>
  );
};

export default SavedFiltersList;
